'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Cookie } from 'lucide-react';

const CONSENT_COOKIE = 'cookie_consent';
const CONSENT_DAYS = 365;

/**
 * CookieConsentBanner — баннер согласия на cookie (152-ФЗ, SUP-95).
 * Показывается при первом визите. Выбор сохраняется в cookie на 1 год.
 */
export function CookieConsentBanner() {
  // Lazy init: проверяем cookie при первом рендере (без effect → нет cascading render).
  const [visible, setVisible] = useState(() => {
    if (typeof document === 'undefined') return false;
    return !getCookie(CONSENT_COOKIE);
  });

  const setConsent = (value: 'all' | 'necessary') => {
    setCookie(CONSENT_COOKIE, value, CONSENT_DAYS);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Согласие на использование cookie"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card px-4 py-4 shadow-lg md:px-6"
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto flex max-w-4xl flex-col items-start gap-3 md:flex-row md:items-center md:gap-4">
        <Cookie className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="flex-1 text-sm text-muted-foreground">
          Мы используем cookie для работы сервиса и аналитики. Продолжая, вы соглашаетесь с{' '}
          <Link href="/privacy#cookies" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            политикой обработки персональных данных
          </Link>
          .
        </p>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConsent('necessary')}
            data-testid="cookie-consent-necessary"
          >
            Только необходимые
          </Button>
          <Button
            size="sm"
            onClick={() => setConsent('all')}
            data-testid="cookie-consent-accept"
          >
            Принять все
          </Button>
        </div>
      </div>
    </div>
  );
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}
